import { PlayerScenarioRunnerAPI } from "@manager/playerManager/PlayerManager";
import { inject, injectable } from "inversify";
import ReactDOM from "react-dom";
import { VintomError } from "../../api/model/error/VintomError";
import { ScenarioElement } from "../../model/playerConfiguration/ScenarioElement";
import { Subtitle } from "../../model/playerConfiguration/Subtitle";
import { PlaybackData } from "../../model/playerState/PlaybackData";
import { ScenarioElementPlayOptions } from "../../model/scenarioElementPlayOptions/ScenarioElementPlayOptions";
import { ScenarioGlobalState } from "../../model/scenarioGlobalState/ScenarioGlobalState";
import { SubtitlesText } from "../../model/subtitles/SubtitlesText";
import { TYPES } from "../di/types";
import { ResourcesCache } from "../loader/scenarioLoader/ResourcesCache";
import { PreferencesStorage } from "../preferencesStorage/PreferencesStorage";
import { ControlsLayerManager } from "./layerManager/ControlsLayerManager";
import { CTALayerManager } from "./layerManager/CTALayerManager";
import { DependentAnimationLayerManager } from "./layerManager/DependentAnimationLayerManager";
import { PlayerContainerManager } from "./layerManager/PlayerContainerManager";
import { PosterLayerManager } from "./layerManager/PosterLayerManager";
import { VideoLayerManager } from "./layerManager/VideoLayerManager";
import { ScenarioExecutor } from "./ScenarioExecutor";
import { VideoPreloader } from "../../features/video-preloading/interfaces/VideoPreloader";
import { VideoLayer } from "@manager/layer/VideoLayer";
import { PlayerAwareVideoJsPreloader } from "../../features/video-preloading/preloaders/PlayerAwareVideoJsPreloader";
import { DebugLayerHandle } from "../../components/layer/DebugLayerComponent";
import { PlayerMusicManager } from "@manager/scenarioRunner/musicManager/PlayerSyncedMusicManager";
import { Looper } from "../scenarioRunner/looper/Looper";
import { BrowserFeatureDetector } from "../../utils/BrowserFeatureDetector";

@injectable()
export class VideoDrivenScenarioExecutorImpl implements ScenarioExecutor {
  private playerAPI: PlayerScenarioRunnerAPI;
  private animationFrameHandle: number;
  private scenarioGlobalState: ScenarioGlobalState;
  private videoPreloader?: VideoPreloader;
  private looper: Looper;

  constructor(
    @inject(TYPES.scenarioRunner.layerManager.PlayerContainerManager) private playerContainerManager: PlayerContainerManager,
    @inject(TYPES.scenarioRunner.layerManager.VideoLayerManager) private videoLayerManager: VideoLayerManager,
    @inject(TYPES.scenarioRunner.layerManager.DependentAnimationLayerManager) private animationLayerManager: DependentAnimationLayerManager,
    @inject(TYPES.scenarioRunner.layerManager.CTALayerManager) private ctaLayerManager: CTALayerManager,
    @inject(TYPES.scenarioRunner.layerManager.ControlsLayerManager) private controlsLayerManager: ControlsLayerManager,
    @inject(TYPES.scenarioRunner.layerManager.PosterLayerManager) private posterLayerManager: PosterLayerManager,
    @inject(TYPES.music.MusicManager) private playerMusicManager: PlayerMusicManager,
    @inject(TYPES.layer.VideoLayer) private videoLayer: VideoLayer,
    @inject(TYPES.preferencesStorage.PreferencesStorage) private preferencesStorage: PreferencesStorage,
    @inject(TYPES.scenarioRunner.playbackInformation) private playbackInformation: DebugLayerHandle,
  ) {
    this.looper = new Looper(videoLayerManager);
    this.initialize();
  }

  private initialize() {
    if (this.preferencesStorage.getPreferences().isDebugModeOpen) {
      this.playerContainerManager.toggleDebugMode();
    }
  }

  setPlayerAPIDelegate(playerAPI: PlayerScenarioRunnerAPI) {
    this.playerAPI = playerAPI;
  }

  cancel(): void {
    if (this.animationFrameHandle !== undefined) {
      cancelAnimationFrame(this.animationFrameHandle);
    }
  }

  intializeAndRun(
    scenarioGlobalState: ScenarioGlobalState,
    scenarioResources: Promise<ResourcesCache>,
    options: ScenarioElementPlayOptions,
  ): Promise<void> {
    this.scenarioGlobalState = scenarioGlobalState;
    return this.initializeScenarioElement(scenarioGlobalState, scenarioResources, options).then(() => {
      this.runSyncLoop();
      if (options.isAutoplay) {
        this.controlsLayerManager.play();
      } else if (options.loop) {
        this.looper.start(options.loop);
        this.controlsLayerManager.loop();
      } else {
        this.controlsLayerManager.pause();
      }
    });
  }

  getPlaybackData(): PlaybackData {
    const isPlaying = this.videoLayerManager.getIsPlaying() && !this.looper.isLooping;
    const time = this.videoLayerManager.getCurrentSeekTime();
    const duration = this.videoLayerManager.getTotalTime();
    const volume = this.videoLayerManager.getVolume();

    return { isPlaying, time, duration, volume };
  }

  getScenarioGlobalState(): ScenarioGlobalState {
    return this.scenarioGlobalState;
  }

  play(): void {
    this.controlsLayerManager.play();
  }

  pause(): void {
    this.controlsLayerManager.pause();
  }

  performAction(ctaId: number, actionId: number, ...args: any[]): void {
    this.ctaLayerManager.performAction(ctaId, actionId, args);
  }

  jumpToScenarioElement(scenarioElement: ScenarioElement, progress: number): void {
    this.videoLayerManager.pause();
    cancelAnimationFrame(this.animationFrameHandle);
    this.posterLayerManager.showPosterOrSnapshot(scenarioElement, progress);
    setTimeout(() => this.playerAPI.loadScenarioElement(scenarioElement, { isAutoplay: true, progress }), 50);
  }

  private async initializeScenarioElement(
    scenarioGlobalState: ScenarioGlobalState,
    scenarioResources: Promise<ResourcesCache>,
    { progress }: ScenarioElementPlayOptions,
  ): Promise<void> {
    this.resetObservers();

    const scenarioPath = scenarioGlobalState.scenarioPath;

    this.posterLayerManager.newScenarioElement(scenarioGlobalState, progress);
    this.videoLayerManager.newScenarioElement(scenarioPath);
    this.controlsLayerManager.newScenarioElement(scenarioPath);
    this.ctaLayerManager.newScenarioElement(scenarioGlobalState);
    this.animationLayerManager.newScenarioElement(scenarioPath);

    this.controlsLayerManager.showLoader("scenarioElementInit", 1000);

    if (this.videoPreloader) {
      this.videoPreloader.cancelAll();
    }

    this.videoPreloader = new PlayerAwareVideoJsPreloader(this.videoLayer.getPlayer());

    const sourcesToPreload = scenarioPath.currentElement.sourcesToPreload?.map(({ sources }) => {
      return sources.find(({ type }) => this.videoLayer.getPlayer().canPlayType(type));
    });

    this.videoPreloader.cancelAll();
    this.videoPreloader.queue(sourcesToPreload ?? []);
    this.videoPreloader.start();

    const resourcesCache = await scenarioResources;
    this.posterLayerManager.resourcesLoaded(resourcesCache);
    if (!this.posterLayerManager.getIsShowingPoster() && progress === 0) {
      this.posterLayerManager.showPoster(scenarioPath.currentElement.poster);
    }
    const duration = await this.videoLayerManager.loadVideoAndGetDuration(progress);
    this.animationLayerManager.resourcesLoaded(resourcesCache, duration);
    this.ctaLayerManager.resourcesLoaded(resourcesCache, duration);
    this.videoLayerManager.changeVolume(scenarioGlobalState.volume);
    this.videoLayerManager.changeMuted(scenarioGlobalState.isMuted);
    if (scenarioGlobalState.scenarioPath.currentElement.hasSubtitles) {
      this.videoLayerManager.activateSubtitles(
        this.scenarioGlobalState.selectedSubtitles && this.scenarioGlobalState.selectedSubtitles.source,
      );
    }

    this.controlsLayerManager.setTotalTime(duration);
    this.controlsLayerManager.changeVolume(scenarioGlobalState.volume);
    this.controlsLayerManager.changeIsMuted(scenarioGlobalState.isMuted);
    if (scenarioGlobalState.scenarioPath.currentElement.hasSubtitles) {
      this.controlsLayerManager.selectSubtitleSource(this.scenarioGlobalState.selectedSubtitles);
    }
  }

  private resetObservers() {
    this.playerContainerManager.registerObserver({
      onPlayerContextMenu: (x: number, y: number) => this.controlsLayerManager.showPlayerInfoTooltip(x, y),
      onPlayerInteractionEnd: () => this.controlsLayerManager.userInteractionEnded(),
      onPlayerInteraction: () => this.controlsLayerManager.userInteractedWithPlayer(),
      onPlayerClick: () => {
        this.controlsLayerManager.userClickedPlayer();
      },
    });

    this.videoLayerManager.registerObserver({
      onEnded: () => {
        this.controlsLayerManager.scenarioElementEnded();
        this.posterLayerManager.scenarioElementEnded();
        this.playerAPI.onScenarioElementEnd();
        // this.musicPlayer.onScenarioElementEnd();
      },
      onError: (error) => this.playerAPI.onError(error),
      onSubtitlesChange: (subtitles: SubtitlesText) => this.controlsLayerManager.displaySubtitles(subtitles),
      onTotalTimeChange: (totalTime) => {
        this.animationLayerManager.setTotalTime(totalTime);
        this.ctaLayerManager.setTotalTime(totalTime);
        this.controlsLayerManager.setTotalTime(totalTime);
      },
      onPlay: () => this.playerAPI.onPlay(),
      onPause: () => {
        this.playerAPI.onPause();
      },
      onWaiting: () => {},
      onPlaying: () => {},
    });

    const shared = {
      onPlay: () => {
        if (this.looper.isLooping) {
          this.looper.stop();

          this.reloadVideoSourcesOnSafari17_4AndHigher();
        }

        this.videoLayerManager.play();
        this.playerMusicManager.play();
      },
      onPause: () => {
        this.videoLayerManager.pause();
        this.playerMusicManager.pause();
      },
    };

    this.playbackInformation.setObserver({
      onPlay: shared.onPlay,
      onPause: shared.onPause,
      onPlaybackSpeedChange: (playbackSpeed: number) => {
        this.videoLayer.getPlayer().playbackRate(playbackSpeed);
        this.playerMusicManager.playbackRate = playbackSpeed;
      },
      onGoToNextFrame: () => {
        shared.onPause();
        this.videoLayerManager.moveBy(1 / 25);
      },
      onGoToPreviousFrame: () => {
        shared.onPause();
        this.videoLayerManager.moveBy(-1 / 25);
      },
    });

    this.controlsLayerManager.registerObserver({
      onPlay: shared.onPlay,
      onPause: shared.onPause,
      onSeekStart: () => {
        this.videoLayerManager.pause();
        this.playerMusicManager.pause();
        this.playerAPI.onSeek();
      },
      onReloadFrame: () => this.videoLayerManager.reloadFrame(),
      onSeekTo: (time) => {
        this.videoLayerManager.moveTo(time);
        this.reloadVideoSourcesOnSafari17_4AndHigher();
      },
      onVolumeChange: (volume: number) => {
        this.preferencesStorage.saveVolumePreference(volume);
        this.scenarioGlobalState = this.scenarioGlobalState.withVolume(volume);
        this.videoLayerManager.changeVolume(volume);
      },
      onMutedChange: (isMuted: boolean) => {
        this.scenarioGlobalState = this.scenarioGlobalState.withMuted(isMuted);
        this.videoLayerManager.changeMuted(isMuted);
        // this.musicPlayer.(isMuted);
      },
      onSubtitleSourceSelected: (subtitle: Subtitle) => {
        this.preferencesStorage.saveSubtitlesPreference(!!subtitle, subtitle && subtitle.label);
        this.scenarioGlobalState = this.scenarioGlobalState.withSelectedSubtitles(subtitle);
        this.videoLayerManager.activateSubtitles(
          this.scenarioGlobalState.selectedSubtitles && this.scenarioGlobalState.selectedSubtitles.source,
        );
      },
      onJumpToScenarioElement: (scElement: ScenarioElement, progress: number) => this.jumpToScenarioElement(scElement, progress),
      onToggleFullscreen: () => this.playerContainerManager.toggleFullscreen(),
      onSendError: (error: VintomError) => this.playerAPI.logUserError(error),
      onToggleDebugMode: (isEnabled) => {
        this.preferencesStorage.saveIsDebugModeOpen(isEnabled);
        this.scenarioGlobalState.withDebugModeOpened(isEnabled);
        this.playerContainerManager.toggleDebugMode();
      },
    });

    this.ctaLayerManager.registerObserver({
      onCTAShow: (cta) => this.playerAPI.onCTAShow(cta),
    });
  }

  private updatePlaybackInformation() {
    const state = this.getScenarioGlobalState();

    const currentDebugPartIndex = state.partsDebugInfo.findIndex((part) => {
      const seekTime = this.videoLayer.getCurrentSeekTime();

      return part.startTime <= seekTime && part.endTime >= seekTime;
    });

    if (currentDebugPartIndex === -1) {
      return;
    }

    const currentDebugPart = state.partsDebugInfo[currentDebugPartIndex]!;

    this.playbackInformation?.setData({
      isPlaying: this.videoLayerManager.getIsPlaying(),
      title: state.campaign,
      videoCode: state.code,
      currentPartDuration: currentDebugPart.endTime - currentDebugPart.startTime,
      currentPartTime: this.videoLayer.getCurrentSeekTime() - currentDebugPart.startTime,
      totalTime: this.videoLayerManager.getCurrentSeekTime(),
      highlightDuration: 500,
      highlightNewPart: true,
      parameters: currentDebugPart.parameters,
      markerName: currentDebugPart.markerName,
      currentPart: currentDebugPartIndex + 1,
    });
  }

  private runSyncLoop() {
    this.animationFrameHandle = requestAnimationFrame(() => {
      try {
        const currentSeekTime = this.videoLayerManager.getCurrentSeekTime();
        const currentFrameTime = this.videoLayerManager.getCurrentFrameTime();
        const bufferedTime = this.videoLayerManager.getBufferedTime();
        const totalTime = this.videoLayerManager.getTotalTime();
        const bandwidth = this.videoLayerManager.getBandwidth();
        const qualityLevels = this.videoLayerManager.getQualityLevels();
        const isFullyLoaded = this.videoLayerManager.getIsFullyLoaded();
        const isWaiting = this.videoLayerManager.getIsWaiting();

        ReactDOM.unstable_batchedUpdates(() => {
          this.ctaLayerManager.moveTo(currentFrameTime);

          this.controlsLayerManager.moveTo(this.looper.isLooping ? 0 : currentSeekTime);
          this.controlsLayerManager.updateBufferedTime(bufferedTime);
          this.controlsLayerManager.updateQualityLevels(qualityLevels);
          this.controlsLayerManager.updateBandwidth(bandwidth);

          this.playerAPI.onProgress(currentFrameTime / totalTime);
          this.looper.update();

          // TODO TEMPORARY ONLY!!!
          try {
            this.updatePlaybackInformation();
          } catch (e) {
            console.error(e);
          }

          if (isFullyLoaded) {
            this.animationLayerManager.moveTo(currentFrameTime);
          }

          if (isFullyLoaded && this.videoLayerManager.getIsPlaying()) {
            this.posterLayerManager.hidePoster();
          }

          if (isWaiting) {
            if (!this.controlsLayerManager.isLoaderPending()) {
              this.controlsLayerManager.showLoader("bufferEmpty", 200);
            }
          } else {
            this.controlsLayerManager.hideLoader();
          }
        });

        this.runSyncLoop();
      } catch (e) {
        this.playerAPI.onError(new VintomError("runtimeError", e.stack.toString()));
      }
    });
  }

  private reloadVideoSourcesOnSafari17_4AndHigher() {
    if (BrowserFeatureDetector.isIOS()) {
      const iosVersion = String(BrowserFeatureDetector.getOS());
      const iosMajorVersion = Number(iosVersion.split(".")[0]);
      const iosMinorVersion = Number(iosVersion.split(".")[1]);
      if (iosMajorVersion === 17 && iosMinorVersion >= 4) {
        this.videoLayerManager.reloadVideo();
      }
    }
  }
}
